import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { api } from "../../../../singleton/axios";
import type { WatchlistEarningsFeedResponse } from "../feed/types";

/** Twenty items a page (§6.1). */
export const FEED_PAGE_SIZE = 20;

/** The head poll's period (§7.3). */
export const FEED_HEAD_REFETCH_MS = 60 * 1000;

const FIFTEEN_MINUTES = 15 * 60 * 1000;

/** Kept an hour unobserved (the app default is 10 minutes), so Back from a long read of a report finds the feed where it was. */
const ONE_HOUR = 60 * 60 * 1000;

// Keyed by list under the two prefixes the watchlist mutations invalidate,
// so adding or removing a company refreshes the feed and its head.
export const earningsFeedKey = (watchlistId: string | null) =>
  ["watchlist-earnings-feed", watchlistId ?? "all"] as const;

export const earningsFeedHeadKey = (watchlistId: string | null) =>
  ["watchlist-earnings-feed-head", watchlistId ?? "all"] as const;

type FeedKey = ReturnType<typeof earningsFeedKey>;
export type FeedPages = InfiniteData<
  WatchlistEarningsFeedResponse,
  string | null
>;

const status = (error: unknown): number | undefined =>
  (error as AxiosError | null)?.response?.status;

/** A 4xx is an answer, so it is never retried. */
const retry = (failureCount: number, error: unknown): boolean => {
  const code = status(error);
  if (code !== undefined && code >= 400 && code < 500) return false;
  return failureCount < 3;
};

const fetchPage = async (
  queryClient: QueryClient,
  watchlistId: string | null,
  before: string | null,
  signal: AbortSignal,
): Promise<WatchlistEarningsFeedResponse> => {
  const params: Record<string, string | number> = { limit: FEED_PAGE_SIZE };
  if (watchlistId !== null) params.watchlistId = watchlistId;
  if (before !== null) params.before = before;
  try {
    const { data } = await api.get<WatchlistEarningsFeedResponse>(
      "/watchlist-earnings-feed",
      { params, signal },
    );
    return data;
  } catch (error) {
    // The list was deleted on another tab or device: refreshing the lists
    // lets the selection fall back to a live one.
    if (status(error) === 404)
      void queryClient.invalidateQueries({ queryKey: ["watchlists"] });
    throw error;
  }
};

interface UseEarningsFeedOptions {
  /** One list; null or absent for every list the user owns. */
  watchlistId?: string | null;
  enabled?: boolean;
}

/**
 * The Earnings Hub's report cards, newest report first, twenty a page behind
 * the API's report-time cursor. Loaded pages never refetch on a timer or on
 * focus (a deep scroll would multiply that); new reports and fresh counts
 * arrive through the head query instead (`useEarningsFeedHead`).
 */
export const useEarningsFeed = ({
  watchlistId = null,
  enabled = true,
}: UseEarningsFeedOptions = {}) => {
  const queryClient = useQueryClient();
  return useInfiniteQuery<
    WatchlistEarningsFeedResponse,
    AxiosError,
    FeedPages,
    FeedKey,
    string | null
  >({
    queryKey: earningsFeedKey(watchlistId),
    initialPageParam: null,
    queryFn: ({ pageParam, signal }) =>
      fetchPage(queryClient, watchlistId, pageParam, signal),
    getNextPageParam: (last) => last.meta.nextCursor,
    enabled,
    staleTime: FIFTEEN_MINUTES,
    gcTime: ONE_HOUR,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    retry,
  });
};

interface UseEarningsFeedHeadOptions {
  watchlistId?: string | null;
  enabled?: boolean;
  /** The feed's own first page: the head starts from it rather than asking again. */
  seed?: WatchlistEarningsFeedResponse;
}

/**
 * Page one again, every minute while the page is visible and on focus, so
 * new items can be offered without moving what the reader is on. The same
 * request as the feed's first page, so the same server cache entry. It
 * never renders directly.
 */
export const useEarningsFeedHead = ({
  watchlistId = null,
  enabled = true,
  seed,
}: UseEarningsFeedHeadOptions = {}) => {
  const queryClient = useQueryClient();
  return useQuery<WatchlistEarningsFeedResponse, AxiosError>({
    queryKey: earningsFeedHeadKey(watchlistId),
    queryFn: ({ signal }) => fetchPage(queryClient, watchlistId, null, signal),
    enabled,
    initialData: seed,
    staleTime: 0,
    gcTime: ONE_HOUR,
    refetchOnMount: false,
    refetchOnWindowFocus: true,
    refetchInterval: FEED_HEAD_REFETCH_MS,
    refetchIntervalInBackground: false,
    retry,
  });
};

/** Apply the head: the feed resets to it as its one page; deeper pages reload as the reader scrolls. */
export const applyFeedHead = (
  queryClient: QueryClient,
  watchlistId: string | null,
  head: WatchlistEarningsFeedResponse,
): void => {
  queryClient.setQueryData<FeedPages>(earningsFeedKey(watchlistId), {
    pages: [head],
    pageParams: [null],
  });
};
